/**
 * Hand a kid their money. Writes a negative allowance_ledger row via the Worker,
 * which refuses to pay out more than is actually banked. Adults only.
 */
import { useState, type FormEvent } from 'react';
import { api, ApiRequestError, money, parseMoney } from '../../lib/api';
import type { AllowanceBalance, MemberSummary } from '../../shared/types';
import { Avatar, Modal, btnPrimary, btnSecondary, cx, fieldInput, fieldLabel } from './shared';

export default function PayoutForm({
  member,
  balance,
  onClose,
  onSaved,
}: {
  member: MemberSummary;
  balance: AllowanceBalance;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [amount, setAmount] = useState((balance.balance / 100).toFixed(2));
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const cents = parseMoney(amount);
    if (cents === null || cents <= 0) {
      setError('Enter an amount, like 12.50.');
      return;
    }
    if (cents > balance.balance) {
      setError(`${member.name} only has ${money(balance.balance)} banked.`);
      return;
    }

    setSaving(true);
    try {
      await api.post('/chores/allowance/payout', {
        member_id: member.id,
        amount: cents,
        note: note.trim() || undefined,
      });
      await onSaved();
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiRequestError ? err.message : 'That did not save. Try again in a second.'
      );
      setSaving(false);
    }
  }

  return (
    <Modal title={`Pay ${member.name}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div className="flex items-center gap-3 rounded-xl bg-surface-sunk p-3">
          <Avatar member={member} />
          <div>
            <p className="text-sm font-medium text-ink">{money(balance.balance)} banked</p>
            {balance.pending > 0 ? (
              <p className="text-xs text-ink-soft">
                {money(balance.pending)} still waiting on your approval — approve it first if you
                want to include it.
              </p>
            ) : (
              <p className="text-xs text-ink-faint">Nothing else pending.</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="payout-amount" className={fieldLabel}>
            Amount to hand over
          </label>
          <div className="relative">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-faint"
            >
              $
            </span>
            <input
              id="payout-amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className={cx(fieldInput, 'pl-7')}
              inputMode="decimal"
              required
              autoFocus
            />
          </div>
          <button
            type="button"
            onClick={() => setAmount((balance.balance / 100).toFixed(2))}
            className="mt-2 text-sm font-medium text-sage underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
          >
            Pay all {money(balance.balance)}
          </button>
        </div>

        <div>
          <label htmlFor="payout-note" className={fieldLabel}>
            Note <span className="font-normal text-ink-faint">(optional)</span>
          </label>
          <input
            id="payout-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={fieldInput}
            placeholder="Cash for the school trip"
            maxLength={80}
          />
        </div>

        {error ? (
          <p role="alert" className="rounded-xl bg-clay-soft px-3 py-2 text-sm text-clay">
            {error}
          </p>
        ) : null}

        <div className="flex gap-2 pt-1">
          <button
            type="submit"
            disabled={saving || balance.balance <= 0}
            className={cx(btnPrimary, 'flex-1')}
          >
            {saving ? 'Saving…' : 'Record payout'}
          </button>
          <button type="button" onClick={onClose} className={btnSecondary}>
            Cancel
          </button>
        </div>
        <p className="text-xs text-ink-faint">
          This records that you handed the money over. It comes straight off their balance.
        </p>
      </form>
    </Modal>
  );
}
